import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

const CODE_LENGTH = 6;

function PinwheelLogo({ size = 34 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

export default function VerifyScreen() {
    const router = useRouter();
    const { verifyOtp, pendingPhone, requestOtp } = useAuth();

    const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(60);
    const [showResendOptions, setShowResendOptions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const inputRefs = useRef<(TextInput | null)[]>([]);

    // Countdown timer
    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }
        const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    // Auto-submit when all digits filled
    const handleVerify = useCallback(
        async (fullCode: string) => {
            if (loading) {
                return;
            }
            setLoading(true);
            setError(null);

            const result = await verifyOtp(fullCode);
            setLoading(false);

            if (!result.success) {
                setError(result.error ?? "Invalid code");
                setCode(Array(CODE_LENGTH).fill(""));
                setActiveIndex(0);
                inputRefs.current[0]?.focus();
            }
        },
        [loading, verifyOtp]
    );

    function handleDigitChange(index: number, value: string) {
        setError(null);

        // Handle paste
        if (value.length > 1) {
            const pasted = value.replace(/[^\d]/g, "").slice(0, CODE_LENGTH);
            if (pasted.length === CODE_LENGTH) {
                const newCode = pasted.split("");
                setCode(newCode);
                setActiveIndex(CODE_LENGTH - 1);
                inputRefs.current[CODE_LENGTH - 1]?.focus();
                handleVerify(pasted);
                return;
            }
        }

        const digit = value.replace(/[^\d]/g, "").slice(-1);
        const newCode = [...code];
        newCode[index] = digit;
        setCode(newCode);

        if (digit && index < CODE_LENGTH - 1) {
            setActiveIndex(index + 1);
            inputRefs.current[index + 1]?.focus();
        }

        const full = newCode.join("");
        if (full.length === CODE_LENGTH && !newCode.includes("")) {
            handleVerify(full);
        }
    }

    function handleKeyPress(index: number, key: string) {
        if (key === "Backspace" && !code[index] && index > 0) {
            const newCode = [...code];
            newCode[index - 1] = "";
            setCode(newCode);
            setActiveIndex(index - 1);
            inputRefs.current[index - 1]?.focus();
        }
    }

    async function handleResendSms() {
        if (!pendingPhone || resendCooldown > 0) {
            return;
        }
        setShowResendOptions(false);
        setResendCooldown(60);
        setCode(Array(CODE_LENGTH).fill(""));
        setActiveIndex(0);
        await requestOtp(pendingPhone);
        inputRefs.current[0]?.focus();
    }

    const maskedPhone = pendingPhone ? `(***) ***-${pendingPhone.slice(-4)}` : "your phone";

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAvoidingView
                style={styles.keyboardView}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                {/* Back */}
                <TouchableOpacity
                    onPress={() => router.replace("/(auth)/login")}
                    hitSlop={12}
                    style={styles.backButton}
                >
                    <MaterialCommunityIcons
                        name="arrow-left"
                        size={22}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>

                <View style={styles.content}>
                    {/* Logo */}
                    <View style={styles.logoArea}>
                        <PinwheelLogo size={52} />
                    </View>

                    <Text style={styles.title}>Verify your number</Text>
                    <Text style={styles.subtitle}>Code sent to {maskedPhone}</Text>

                    {/* Code input */}
                    <View style={styles.codeRow}>
                        {code.map((digit, i) => (
                            <TextInput
                                key={i}
                                ref={(ref) => {
                                    inputRefs.current[i] = ref;
                                }}
                                style={[
                                    styles.codeBox,
                                    digit ? styles.codeBoxFilled : null,
                                    activeIndex === i && !error ? styles.codeBoxActive : null,
                                    error ? styles.codeBoxError : null
                                ]}
                                value={digit}
                                onChangeText={(v) => handleDigitChange(i, v)}
                                onKeyPress={({ nativeEvent }) => handleKeyPress(i, nativeEvent.key)}
                                onFocus={() => setActiveIndex(i)}
                                keyboardType="number-pad"
                                maxLength={i === 0 ? CODE_LENGTH : 1}
                                autoFocus={i === 0}
                                selectTextOnFocus
                                editable={!loading}
                            />
                        ))}
                    </View>

                    {/* Loading state */}
                    {loading && (
                        <View style={styles.loadingRow}>
                            <ActivityIndicator size="small" color={colors.teal400} />
                            <Text style={styles.verifyingText}>Verifying...</Text>
                        </View>
                    )}

                    {error && (
                        <View style={styles.errorRow}>
                            <MaterialCommunityIcons
                                name="alert-circle"
                                size={14}
                                color={colors.error500}
                            />
                            <Text style={styles.errorText}>{error}</Text>
                        </View>
                    )}

                    {/* Resend options */}
                    <View style={styles.resendArea}>
                        {!showResendOptions ? (
                            <TouchableOpacity
                                onPress={() => setShowResendOptions(true)}
                                disabled={resendCooldown > 0}
                            >
                                <Text
                                    style={[
                                        styles.resendLink,
                                        resendCooldown > 0 && styles.resendLinkDisabled
                                    ]}
                                >
                                    {resendCooldown > 0
                                        ? `Didn't get code? Resend (${resendCooldown}s)`
                                        : "Didn't get code? Resend"}
                                </Text>
                            </TouchableOpacity>
                        ) : (
                            <View style={styles.resendOptions}>
                                <TouchableOpacity
                                    style={styles.resendOption}
                                    onPress={handleResendSms}
                                >
                                    <MaterialCommunityIcons
                                        name="message-text-outline"
                                        size={20}
                                        color={colors.teal400}
                                    />
                                    <Text style={styles.resendOptionText}>Resend SMS</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={styles.resendOption}
                                    onPress={() => {
                                        // TODO: call me instead
                                        setShowResendOptions(false);
                                    }}
                                >
                                    <MaterialCommunityIcons
                                        name="phone-outline"
                                        size={20}
                                        color={colors.teal400}
                                    />
                                    <Text style={styles.resendOptionText}>Call me instead</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={styles.resendOption}
                                    onPress={() => {
                                        setShowResendOptions(false);
                                        router.back();
                                    }}
                                >
                                    <MaterialCommunityIcons
                                        name="arrow-left"
                                        size={20}
                                        color={colors.textTertiary}
                                    />
                                    <Text
                                        style={[
                                            styles.resendOptionText,
                                            { color: colors.textTertiary }
                                        ]}
                                    >
                                        Use a different number
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        )}
                    </View>
                </View>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    keyboardView: {
        flex: 1
    },
    backButton: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        alignSelf: "flex-start"
    },
    content: {
        flex: 1,
        paddingHorizontal: spacing.xl,
        justifyContent: "center",
        alignItems: "center"
    },
    logoArea: {
        marginBottom: spacing["2xl"]
    },
    title: {
        fontSize: 22,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.sm,
        marginBottom: spacing["2xl"],
        textAlign: "center"
    },
    codeRow: {
        flexDirection: "row",
        gap: spacing.sm,
        justifyContent: "center",
        marginBottom: spacing.lg
    },
    codeBox: {
        width: 48,
        height: 56,
        borderRadius: radius.md,
        borderWidth: 2,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        textAlign: "center",
        fontSize: 24,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    codeBoxFilled: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    codeBoxActive: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    codeBoxError: {
        borderColor: colors.error400
    },
    loadingRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginBottom: spacing.md
    },
    verifyingText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.teal400
    },
    errorRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xs,
        marginBottom: spacing.md
    },
    errorText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.error500
    },
    resendArea: {
        alignItems: "center",
        marginTop: spacing.xl,
        width: "100%"
    },
    resendLink: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.teal400
    },
    resendLinkDisabled: {
        color: colors.textTertiary
    },
    resendOptions: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.sm,
        width: "100%",
        ...shadows.sm
    },
    resendOption: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base
    },
    resendOptionText: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    }
});
